import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdCondition
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_DRG_CODE_EXISTS_CONDITION_ID = 'drgCodeExists';
export const FilterConditionDrgCodeExistsPluginType: FilterConditionPluginSpecification<
  boolean,
  FilterConditionDrgCodeExistsDto,
  BdDrgCodeExistsCondition
> = {
  type: 'drgCodeExists',
  category: 'case',
  displayName: { de: 'DRG vorhanden' },
  sortingIndex: 11,
  occurrence: 'multiple',
  fromObject(
    obj: FilterConditionDrgCodeExistsDto,
  ): FilterConditionPluginImpl<boolean> {
    return new FilterConditionDrgCodeExistsPlugin(obj.isNotNull ?? obj._isNotNull);
  },
  fromCondition(
    jsonObj: BdDrgCodeExistsCondition,
  ): FilterConditionPluginImpl<boolean> {
    return new FilterConditionDrgCodeExistsPlugin(jsonObj.isNotNull);
  },
  newInstance(): FilterConditionPluginImpl<boolean> {
    return new FilterConditionDrgCodeExistsPlugin(true);
  },
};

export class FilterConditionDrgCodeExistsPlugin extends FilterConditionPluginImpl<
  boolean,
  FilterConditionDrgCodeExistsDto
> {
  static override Specification: FilterConditionPluginSpecification<
    boolean,
    FilterConditionDrgCodeExistsDto,
    BdDrgCodeExistsCondition
  > = FilterConditionDrgCodeExistsPluginType;

  constructor(private _isNotNull: boolean) {
    super(FilterConditionDrgCodeExistsPluginType.type);
  }

  toCondition(): BdCondition {
    return new BdDrgCodeExistsCondition(this._isNotNull);
  }

  toJSON(): FilterConditionDrgCodeExistsDto | null {
    return {
      type: this.type,
      isNotNull: this._isNotNull,
    };
  }

  toText(): string | null {
    return 'DRG vorhanden';
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    if (this._isNotNull === null || this._isNotNull === undefined) {
      return { title, items: [] };
    }
    return { title, items: [this._isNotNull ? 'Ja' : 'Nein'] };
  }

  toValue(): boolean {
    return this._isNotNull;
  }

  override equals(other: FilterConditionDrgCodeExistsPlugin): boolean {
    return super.equals(other) && this._isNotNull === other._isNotNull;
  }

  override valid(): boolean {
    return this._isNotNull !== null && this._isNotNull !== undefined;
  }

  override validationToText(): string {
    if (this.valid()) {
      return '';
    } else {
      return 'Ja/Nein auswählen';
    }
  }

  clone(): FilterConditionDrgCodeExistsPlugin {
    return new FilterConditionDrgCodeExistsPlugin(this._isNotNull);
  }
}

export class BdDrgCodeExistsCondition extends BdCondition {
  constructor(public isNotNull: boolean) {
    super(BD_DRG_CODE_EXISTS_CONDITION_ID);
  }

  toJSON(): FilterConditionDto {
    return {
      [this.id]: {
        isNotNull: this.isNotNull,
      },
    };
  }
}

interface FilterConditionDrgCodeExistsDto {
  type: string;
  isNotNull: boolean;
  _isNotNull?: boolean;
}
